import * as React from 'react';
import 'twin.macro';
import { useSetAtom } from 'jotai';

import { getAssetSrc } from '@restworld/utils-common';

import { isPDFReadyAtom } from '.';

interface IRestaurantShowCaseQRProps {
  restaurantDetailsQR?: string;
}

const RestaurantShowCaseFullPageQRA4 = React.forwardRef<HTMLDivElement, IRestaurantShowCaseQRProps>(
  ({ restaurantDetailsQR = '' }, ref) => {
    const setIsPDFReady = useSetAtom(isPDFReadyAtom);
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    // Set a scale factor for high resolution (e.g., 2x)
    const SCALE = 3;

    const loadImage = (src: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = src;
        img.onload = () => resolve(img);
        img.onerror = reject;
      });
    };

    const drawImageRotated = (
      ctx: CanvasRenderingContext2D,
      img: HTMLImageElement,
      [x, y, width, height]: [number, number, number, number]
    ) => {
      ctx.save();
      ctx.translate(x + width / 2, y + height / 2);
      // ctx.rotate(-Math.PI / 2);
      ctx.drawImage(img, -width / 2, -height / 2, width, height);
      ctx.restore();
    };

    React.useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // maintain a a5 aspect ratio for the canvas
      const canvasWidth = 595; // A5 width in points
      const canvasHeight = 842; // A5 height in points
      const qrTemplateSrc = getAssetSrc('/utils-assets/restaurant-qr-template.png');

      // Set high resolution size
      canvas.width = canvasWidth * SCALE;
      canvas.height = canvasHeight * SCALE;
      // Set CSS size for display
      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;

      ctx.setTransform(SCALE, 0, 0, SCALE, 0, 0);

      loadImage(qrTemplateSrc)
        .then((qrTemplate) => {
          ctx.drawImage(qrTemplate, 0, 0, canvasWidth, canvasHeight);
        })
        .catch((error) => console.error('Error loading qr-template image', error));
    }, []);

    React.useEffect(() => {
      setTimeout(() => {
        if (!restaurantDetailsQR) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const qrImageDrawCoords: [number, number, number, number][] = [[80, 457, 195, 195]];

        loadImage(restaurantDetailsQR)
          .then((qrImage) => {
            qrImageDrawCoords.forEach((coords) => drawImageRotated(ctx, qrImage, coords));
            setIsPDFReady(true);
          })
          .catch((error) => console.error('Error loading qr image', error));
      }, 750);
    }, [restaurantDetailsQR, setIsPDFReady]);

    return (
      <div ref={ref}>
        <div tw="w-full h-full">
          <canvas ref={canvasRef} />
        </div>
      </div>
    );
  }
);

export default RestaurantShowCaseFullPageQRA4;
